/**
 * Popover body for the RMA details column settings: sort direction and date field option lists.
 * Presentational only - no store access.
 */
import FontBody3 from "../../../../../../components/fonts/font-body3/font-body3";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import IconInfo from "../../../../../../components/icons/icon-info/icon-info";
import { Tooltip } from "../../../../../../components/tooltip/tooltip";
import { SORT_TYPES } from "../../../../../../components/table/table.types";
import useDashboardTranslation from "../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { RETURNS_DATE_FIELD } from "../../../../../../services/api/ui-settings-service";
import { IReturnsDateFieldOption } from "../../../../../../stores/ui-settings-store";
import { IDateColumnSortOption } from "../../../stores/rma-tabs-paged-list-store";
import RmaListFilterRadioOption, {
    RmaListFilterOptionId,
} from "../../rma-list-filter-control/contents/radio-option/rma-list-filter-radio-option";
import { RmaListFilterRadioGroupProvider } from "../../rma-list-filter-control/contents/radio-option/rma-list-filter-radio-group-context";
import "./rma-list-table-settings-content.scss";

export interface IRmaListTableSettingsContentProps {
    /** Active sort direction, or null while the list is not sorted by the date column. */
    sortDir: SORT_TYPES | null;
    sortOptions: IDateColumnSortOption[];
    onSortDirChange: (sortDir: SORT_TYPES) => void;
    dateField: RETURNS_DATE_FIELD;
    dateFieldOptions: IReturnsDateFieldOption[];
    onDateFieldChange: (dateField: RETURNS_DATE_FIELD) => void;
}

const RmaListTableSettingsContent: React.FC<IRmaListTableSettingsContentProps> = ({
    sortDir,
    sortOptions,
    onSortDirChange,
    dateField,
    dateFieldOptions,
    onDateFieldChange,
}) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-table-settings";

    return (
        <div className="rma-list-table-settings-content">
            <div className="rma-list-table-settings-content__section">
                <div className="rma-list-table-settings-content__section-header">
                    <FontBody3 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY3_50}>
                        {getTranslation("SORT_BY", scope)}
                    </FontBody3>
                    <Tooltip
                        content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                {getTranslation("DATE_COLUMN_TOOLTIP", scope)}
                            </FontBody2>
                        }
                    >
                        <IconInfo color="var(--color-neutral-3)" />
                    </Tooltip>
                </div>
                <RmaListFilterRadioGroupProvider
                    name="rma-list-table-settings-sort"
                    value={sortDir}
                    onSelect={(id: RmaListFilterOptionId) => onSortDirChange(id as SORT_TYPES)}
                >
                    <div role="radiogroup">
                        {sortOptions.map((option) => (
                            <RmaListFilterRadioOption key={option.id} option={option} />
                        ))}
                    </div>
                </RmaListFilterRadioGroupProvider>
            </div>

            <div className="rma-list-table-settings-content__section">
                <div className="rma-list-table-settings-content__section-header">
                    <FontBody3 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY3_50}>
                        {getTranslation("DISPLAY_DATE_BASED_ON", scope)}
                    </FontBody3>
                </div>
                <RmaListFilterRadioGroupProvider
                    name="rma-list-table-settings-date-field"
                    value={dateField}
                    onSelect={(id: RmaListFilterOptionId) => onDateFieldChange(id as RETURNS_DATE_FIELD)}
                >
                    <div role="radiogroup">
                        {dateFieldOptions.map((option) => (
                            <RmaListFilterRadioOption key={option.id} option={option} />
                        ))}
                    </div>
                </RmaListFilterRadioGroupProvider>
            </div>
        </div>
    );
};

export default RmaListTableSettingsContent;
